/**
 * Setswana catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Setswana at a mathematician's register. **Warrant → «Netefatso»**
 * (verification/warrant — flagged). Scientific loanwords are used where Setswana standardly
 * borrows (logic → Lojiki, modal → Modale, heuristic, scalar, geometry). W/M grades stay Latin
 * (`::numbering-system/latn`). CLDR cardinal categories are one / other; the noun uses the
 * se-/di- class (thibelo → dithibelo). `frameActive` declares one/other plus an explicit `=0`.
 * Several abstract terms are flagged "expert-review suggested." Per INV-STABLE-ID a wrong
 * translation is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const TN_CATALOG = {
    locale: 'tn',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Patlisiso',
        [P2.sectionClaims]: 'Dipolelo',
        [P2.sectionIntegrity]: 'Boikanyego',
        [P2.axisWarrantLabel]: 'Netefatso',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirikale',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Tlhaloso} interp {Thanololo} testable {E ka lekwang} tested {E lekilwe} refuted {E ganediwe} program {Lenaneo la dipatlisiso} philos {Ya filosofi} other {Ga e itsiwe}}',
        [P2.axisModalLabel]: 'Modale',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Boemo jwa maatla {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'E ka batlisisiwang',
        [P2.stateRefuted]: 'E ganediwe',
        [P2.stateOutside]: 'Kwa ntle ga lefelo',
        [P2.verdictConflict]: 'E ganana le {constraints}',
        [P2.verdictOutside]: 'E tlhoka {missing}',
        [P2.lossRegisterPlain]: 'E dirilwe motlhofo mo puong e e tlwaelegileng — boammaaruri bongwe bo tlogetswe.',
        [P2.lossRegisterKid]: 'E dirilwe motlhofo go babadi ba bannye — dintlha tse dintsi di tlogetswe.',
        [P2.lossProfileDetail]: 'Profaele e e feletseng ya Netefatso / Empirikale / Modale e akareditswe fa.',
        [P2.lossConflictDetail]: 'Dintlha tsa kgotlhang di khutshwafaditswe mo ponong eno.',
        [P2.laneVerified]: 'E netefaditswe',
        [P2.laneUnverified]: 'Ga e a netefadiwa',
        [P2.laneConflict]: 'Kgotlhang e supilwe',
        [P2.constraintLogic]: 'Lojiki',
        [P2.constraintLightBends]: 'Lesedi le a kobega mo tshimong ya maatla a kgogedi',
        [P2.constraintParallelPostulate]: 'Postulate ya mela e e tshwanang',
        [P2.claimScalarGravity]: 'Tlhagiso ya scalar ya maatla a kgogedi',
        [P2.claimNonEuclidean]: 'Geometri e e seng ya Euclid',
        [P2.claimSubstrateSr]: 'Thanololo ya substrate ya kamano e e kgethegileng',
        [P2.edgeExtObservation]: 'Tlhokomelo ya kwa ntle',
        [P2.edgeResourceSample]: 'Motswedi wa dipalo o o abelanwang',
        [P2.edgeHeuristicSample]: 'Kaelo ya heuristic',
        [P2.edgeSharedSample]: 'Motheo o o abelanwang',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Dithibelo',
        [P2.sectionEdges]: 'Dintlha',
        [P2.sectionFrame]: 'Foreimi',
        [P2.sectionExposure]: 'Ponatshego',
        [P2.statusAdoptedFormal]: 'E amogetswe (semmuso)',
        [P2.statusAdoptedInterpretive]: 'E amogetswe (ka thanololo)',
        [P2.statusSuspended]: 'E emisitswe',
        [P2.edgeEffectConstraint]: 'E dira thibelo (e ka gana)',
        [P2.edgeEffectResource]: 'Motswedi — ga e na tlhotlheletso mo ditshwetsong',
        [P2.edgeEffectHeuristic]: 'Heuristic — ga e na tlhotlheletso mo ditshwetsong',
        [P2.edgeEffectShared]: 'Motheo o o abelanwang — ga e na tlhotlheletso mo ditshwetsong',
        [P2.frameActive]: '{count, plural, =0 {Ga go na dithibelo tse dirang} one {# thibelo e dirang} other {# dithibelo tse dirang}}',
        [P2.exposureNone]: 'Ga go na go ikaega ka thanololo',
        [P2.exposureDep]: 'E ikaegile mo {id}',
        [P2.laneTrackMachine]: 'Motšhine',
        [P2.laneTrackExpert]: 'Setsebi',
        [P2.laneTrackCommunity]: 'Baagi',
        [P2.expertAudited]: 'E sekasekilwe',
        [P2.expertInProgress]: 'E a tsamaya',
        [P2.expertNotReviewed]: 'Ga e a sekasekwa',
        [P2.communityFlags]: 'Go na le matshwao',
        [P2.communityNone]: 'Ga go na matshwao',
        [P2.controlChallenge]: 'Tlhola',
        [P2.controlActivate]: 'Tsosa',
        [P2.controlMakeInterpretive]: 'Dira ya thanololo',
        [P2.controlImportEdge]: 'Tsenya ntlha',
        [P2.controlPromote]: 'Tlhatlosa',
        [P2.controlUndo]: 'Phimola',
        [P2.controlRedo]: 'Dira gape',
        [P2.controlRegister]: 'Kwadisa',
        [P2.promoteRejected]: 'Tlhatloso e gannwe',
        [P2.promoteAccepted]: 'Tlhatloso e amogetswe',
        [P2.promoteReasonRefutationTerminal]: 'Bosupi bo ka se fodise kganetso.',
        [P2.promoteReasonKeystone]: 'Tlhatloso ya botlhokwa e tlhoka tshegetso ya empirikale.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Mofuta wa empirikale ga o a tlhomamisiwa.',
        [P2.promoteReasonModalNotFixed]: 'Boemo jwa modale ga bo a tlhomamisiwa.',
        [P2.promoteReasonWarrantNotImproved]: 'Boemo jwa netefatso ga bo a tokafadiwa.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=tn.js.map